/**
 * FONT_STACKS — single source of truth for Font axis preset → Google Font entry.
 *
 * Consumed by:
 *   - shadcn-presets.ts (lookupPreset → ThemeTokens.fonts)
 *   - BlockGridTestPage.tsx (Google Fonts <link> injection + sidebar sanity fallback)
 *   - design-system-preview.tsx (indirectly via useDesignSystem → ThemeTokens)
 *
 * Phase 393 (D-05) — extracted from BlockGridTestPage so the preset lookup
 * and the font-loading <link> tag both reference the same table.
 */

export interface FontStack {
  /** Google Fonts family spec, e.g. 'Inter:wght@400;500;600;700'. */
  google: string
  /** CSS font-family stack, e.g. '"Inter", system-ui, sans-serif'. */
  stack: string
  /** 'sans' | 'mono' | 'serif' — used by lookupPreset to decide which --font-* slot to fill. */
  category: 'sans' | 'mono' | 'serif'
}

export const FONT_STACKS: Record<string, FontStack> = {
  geist: { google: 'Geist:wght@400;500;600;700', stack: '"Geist", system-ui, sans-serif', category: 'sans' },
  inter: { google: 'Inter:wght@400;500;600;700', stack: '"Inter", system-ui, sans-serif', category: 'sans' },
  'noto-sans': { google: 'Noto+Sans:wght@400;500;600;700', stack: '"Noto Sans", system-ui, sans-serif', category: 'sans' },
  'nunito-sans': { google: 'Nunito+Sans:wght@400;500;600;700;800', stack: '"Nunito Sans", system-ui, sans-serif', category: 'sans' },
  figtree: { google: 'Figtree:wght@400;500;600;700', stack: '"Figtree", system-ui, sans-serif', category: 'sans' },
  roboto: { google: 'Roboto:wght@400;500;700', stack: '"Roboto", system-ui, sans-serif', category: 'sans' },
  raleway: { google: 'Raleway:wght@400;500;600;700', stack: '"Raleway", system-ui, sans-serif', category: 'sans' },
  'dm-sans': { google: 'DM+Sans:wght@400;500;600;700', stack: '"DM Sans", system-ui, sans-serif', category: 'sans' },
  'public-sans': { google: 'Public+Sans:wght@400;500;600;700', stack: '"Public Sans", system-ui, sans-serif', category: 'sans' },
  outfit: { google: 'Outfit:wght@400;500;600;700', stack: '"Outfit", system-ui, sans-serif', category: 'sans' },
  oxanium: { google: 'Oxanium:wght@400;500;600;700', stack: '"Oxanium", system-ui, sans-serif', category: 'sans' },
  manrope: { google: 'Manrope:wght@400;500;600;700', stack: '"Manrope", system-ui, sans-serif', category: 'sans' },
  'space-grotesk': { google: 'Space+Grotesk:wght@400;500;600;700', stack: '"Space Grotesk", system-ui, sans-serif', category: 'sans' },
  montserrat: { google: 'Montserrat:wght@400;500;600;700', stack: '"Montserrat", system-ui, sans-serif', category: 'sans' },
  'ibm-plex-sans': { google: 'IBM+Plex+Sans:wght@400;500;600;700', stack: '"IBM Plex Sans", system-ui, sans-serif', category: 'sans' },
  'source-sans-3': { google: 'Source+Sans+3:wght@400;500;600;700', stack: '"Source Sans 3", system-ui, sans-serif', category: 'sans' },
  'instrument-sans': { google: 'Instrument+Sans:wght@400;500;600;700', stack: '"Instrument Sans", system-ui, sans-serif', category: 'sans' },
  'jetbrains-mono': { google: 'JetBrains+Mono:wght@400;500;700', stack: '"JetBrains Mono", ui-monospace, monospace', category: 'mono' },
  'geist-mono': { google: 'Geist+Mono:wght@400;500;700', stack: '"Geist Mono", ui-monospace, monospace', category: 'mono' },
  'noto-serif': { google: 'Noto+Serif:wght@400;500;700', stack: '"Noto Serif", ui-serif, serif', category: 'serif' },
  'roboto-slab': { google: 'Roboto+Slab:wght@400;500;700', stack: '"Roboto Slab", ui-serif, serif', category: 'serif' },
  merriweather: { google: 'Merriweather:wght@400;700', stack: '"Merriweather", ui-serif, serif', category: 'serif' },
  lora: { google: 'Lora:wght@400;500;600;700', stack: '"Lora", ui-serif, serif', category: 'serif' },
  'playfair-display': { google: 'Playfair+Display:wght@400;500;600;700', stack: '"Playfair Display", ui-serif, serif', category: 'serif' },
}
